import React, { useMemo } from 'react';
import { Formik, FieldArray, ArrayHelpers, FormikProps } from 'formik';
import { Form, Select, Input } from 'formik-antd';
import { Row, Col, Button } from 'antd';
import { isNil } from 'lodash';
import * as Yup from 'yup';
import cx from 'classnames';
import { tz } from 'moment-timezone';

import { getFieldLabel } from 'utils/formUtils';
import FormItem from 'components/formik/FormItem';
import DatePicker from 'components/formik/DatePicker';
import { OutreachEnum } from 'api/backofficeApiClient';
import FormikEffect from 'components/formik/FormikEffect';
import { OutreachDate, OutreachFormValues } from './mapUtils';
import { disablePastDates, TIME_OPTIONS, TIME_PERIODS } from 'utils/dateUtils';
import { isAddButtonVisible } from 'utils/fieldArrayUtils';

import styles from './RepresentativeOutreach.module.less';

export interface MemberContacts {
  email: string;
  phone: string;
}

type Props = {
  formRef: any;
  isActive: boolean;
  isRequired: boolean;
  isInternational: boolean;
  memberContacts: MemberContacts;
  onChange: (formik: FormikProps<OutreachFormValues>) => void;
};

const maxCharactersLength = 120;
const maxOutreachDatesLength = 10;

export const isPhoneMethodSelected = (contactMethod?: OutreachEnum) =>
  contactMethod === OutreachEnum.Phone;

const isRemoveButtonVisible = (outreachDates: Array<OutreachDate>) =>
  outreachDates.length > 1;

const outreachDataValidationSchema = {
  date: Yup.date()
    .nullable()
    .min(new Date(), 'Start date should not be in the past'),
  time: Yup.string().nullable(),
  tz: Yup.string().max(
    maxCharactersLength,
    `Time zone should be ${maxCharactersLength} characters maximum`,
  ),
};

const initialValidationSchema = (isInternational: boolean) => ({
  outreachDates: Yup.array().of(
    Yup.object({
      date: Yup.date()
        .nullable()
        .required('Field is required')
        .min(new Date(), 'Start date should not be in the past'),
      time: Yup.string().nullable().required('Field is required'),
      tz: Yup.string()
        .required('Field is required')
        .max(
          maxCharactersLength,
          `Time zone should be ${maxCharactersLength} characters maximum`,
        ),
    }),
  ),
  contactMethod: Yup.number().nullable().required('Field is required'),
  memberPhoneNumber: Yup.string()
    .trim()
    .max(120, 'Phone number should be 120 characters maximum'),
  memberEmail: Yup.string()
    .trim()
    .email('Email format is invalid')
    .max(
      maxCharactersLength,
      `Email should be ${maxCharactersLength} characters maximum`,
    ),
});

const getValidationSchema = (isRequired: boolean, isInternational: boolean) => {
  const schema = { ...initialValidationSchema(isInternational) };

  if (!isRequired) {
    const nonRequiredSchema = {
      ...schema,
      outreachDates: Yup.array().of(Yup.object(outreachDataValidationSchema)),
    };

    return Yup.object(nonRequiredSchema);
  }

  const requiredValidationSchema = {
    ...schema,
    memberEmail: schema.memberEmail.when('contactMethod', {
      is: (channel: OutreachEnum) => !isPhoneMethodSelected(channel),
      then: Yup.string().required('Field is required'),
    }),
    memberPhoneNumber: schema.memberPhoneNumber.when('contactMethod', {
      is: isPhoneMethodSelected,
      then: Yup.string().required('Field is required'),
    }),
    contactMethod: schema.contactMethod.required('Field is required'),
    outreachDates: Yup.array().of(
      Yup.object({
        ...outreachDataValidationSchema,
        date: outreachDataValidationSchema.date.required('Field is required'),
        time: outreachDataValidationSchema.time.required('Field is required'),
        tz: outreachDataValidationSchema.tz.required('Field is required'),
      }),
    ),
  };

  return Yup.object(requiredValidationSchema);
};

export default React.memo((props: Props) => {
  const {
    formRef,
    onChange,
    isActive,
    isRequired,
    memberContacts,
    isInternational,
  } = props;
  const initialValues: OutreachFormValues = {
    contactMethod: OutreachEnum.Phone,
    outreachDates: [
      { date: null, time: '', tz: tz.guess(true), timePeriod: TIME_PERIODS[0] },
    ],
    memberEmail: memberContacts.email || '',
    memberPhoneNumber: memberContacts.phone || '',
  };
  const validationSchema = useMemo(
    () => getValidationSchema(isRequired, isInternational),
    [isRequired, isInternational],
  );

  return (
    <Formik
      innerRef={formRef}
      onSubmit={() => {}}
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {(formik) => (
        <>
          <FormikEffect onChange={onChange} disableOnChange={!isActive} />

          <Form layout="vertical">
            <div className="modal-scroll-wrapper">
              <div className={styles.text}>
                Thanks for Registering! We will be following up with you to
                finalize your account, please provide few availability options
                that work for your schedule for us to coordinate a call.
              </div>

              <Row gutter={20}>
                <Col xs={24} md={12}>
                  <FormItem
                    name="contactMethod"
                    label={getFieldLabel('Best contact method', isRequired)}
                    data-id="contactMethodField"
                  >
                    <Select
                      name="contactMethod"
                      placeholder="Select best contact method"
                    >
                      <Select.Option value={OutreachEnum.Phone}>
                        Phone
                      </Select.Option>
                      <Select.Option value={OutreachEnum.Email}>
                        Email
                      </Select.Option>
                    </Select>
                  </FormItem>
                </Col>

                {!isNil(formik.values.contactMethod) && (
                  <Col xs={24} md={12}>
                    {isPhoneMethodSelected(formik.values.contactMethod) ? (
                      <FormItem
                        data-id="memberPhoneNumberField"
                        label={getFieldLabel('Phone number', isRequired)}
                        name="memberPhoneNumber"
                      >
                        <Input
                          name="memberPhoneNumber"
                          placeholder="Enter phone number"
                          autoComplete="off"
                        />
                      </FormItem>
                    ) : (
                      <>
                        <FormItem
                          name="memberEmail"
                          label={getFieldLabel('Email', isRequired)}
                          data-id="memberEmailField"
                        >
                          <Input name="memberEmail" placeholder="Enter email" autoComplete="off" />
                        </FormItem>

                        <p>
                          We will send you a calendar invite to this email to
                          schedule a call.
                        </p>
                      </>
                    )}
                  </Col>
                )}
              </Row>

              <FieldArray
                name="outreachDates"
                render={(arrayHelpers: ArrayHelpers) => (
                  <Row gutter={20}>
                    {formik.values.outreachDates.length > 0 &&
                      formik.values.outreachDates.map(
                        (outreachDate: OutreachDate, index: number) => (
                          <Col xs={24} md={24} key={index}>
                            <Row gutter={20} align="middle">
                              <Col xs={24} md={12}>
                                <Row gutter={10}>
                                  <Col xs={12} md={12}>
                                    <FormItem
                                      name={`outreachDates.${index}.date`}
                                      label={getFieldLabel('Date', isRequired)}
                                      data-id={`outreachDateField-${index}`}
                                    >
                                      <DatePicker
                                        name={`outreachDates.${index}.date`}
                                        disabledDate={disablePastDates}
                                        showToday={false}
                                        format="MM/DD/YYYY"
                                        placeholder="Select date"
                                        className="full-width"
                                      />
                                    </FormItem>
                                  </Col>

                                  <Col xs={12} md={12}>
                                    <FormItem
                                      name={`outreachDates.${index}.tz`}
                                      label={getFieldLabel(
                                        'Time zone',
                                        isRequired,
                                      )}
                                      data-id={`outreachTzField-${index}`}
                                    >
                                      <Input
                                        name={`outreachDates.${index}.tz`}
                                        placeholder="Enter time zone"
                                        autoComplete="off"
                                      />
                                    </FormItem>
                                  </Col>
                                </Row>
                              </Col>

                              <Col xs={24} md={12}>
                                <Row gutter={10} align="middle">
                                  <Col flex="1 0 0">
                                    <FormItem
                                      name={`outreachDates.${index}.time`}
                                      label={getFieldLabel('Time', isRequired)}
                                      data-id={`outreachTimeField-${index}`}
                                    >
                                      <Select
                                        name={`outreachDates.${index}.time`}
                                        placeholder="Select time"
                                      >
                                        {TIME_OPTIONS.map((option) => (
                                          <Select.Option
                                            key={`select-outreach-time-${option}`}
                                            value={option}
                                          >
                                            {option}
                                          </Select.Option>
                                        ))}
                                      </Select>
                                    </FormItem>
                                  </Col>

                                  <Col flex="1 0 0">
                                    <FormItem
                                      name={`outreachDates.${index}.timePeriod`}
                                      label=" "
                                      data-id={`outreachTimePeriodField-${index}`}
                                    >
                                      <Select
                                        name={`outreachDates.${index}.timePeriod`}
                                      >
                                        {TIME_PERIODS.map((period) => (
                                          <Select.Option
                                            key={`select-outreach-time-period-${period}`}
                                            value={period}
                                          >
                                            {period}
                                          </Select.Option>
                                        ))}
                                      </Select>
                                    </FormItem>
                                  </Col>
                                  <Col>
                                    <div className={styles.action}>
                                      {isAddButtonVisible<OutreachDate>(
                                        formik.values.outreachDates,
                                        index,
                                        maxOutreachDatesLength,
                                      ) && (
                                        <Button
                                          className={cx(
                                            'borderless-btn',
                                            styles.btn,
                                          )}
                                          onClick={() =>
                                            arrayHelpers.push({
                                              date: null,
                                              time: '',
                                              tz: tz.guess(true),
                                              timePeriod: TIME_PERIODS[0],
                                            } as OutreachDate)
                                          }
                                          icon={
                                            <span className="icon icon-plus-filled"></span>
                                          }
                                        />
                                      )}

                                      {isRemoveButtonVisible(
                                        formik.values.outreachDates,
                                      ) && (
                                        <Button
                                          className={cx(
                                            'borderless-btn',
                                            styles.btn,
                                          )}
                                          onClick={() =>
                                            arrayHelpers.remove(index)
                                          }
                                          icon={
                                            <span className="icon icon-closed-filled" />
                                          }
                                        />
                                      )}
                                    </div>
                                  </Col>
                                </Row>
                              </Col>
                            </Row>
                          </Col>
                        ),
                      )}
                  </Row>
                )}
              />
            </div>
          </Form>
        </>
      )}
    </Formik>
  );
});
